import React, { useEffect, useState } from 'react';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import { useTheme } from '../../../../theme-context';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { ANSWER_STATUS } from '../../../../utils/constants/answer-status';
import {
    tryToFetchAllUnansweredQuestions,
    tryToFetchUnansweredQuestions,
} from '../../../../store/brokers/partners/unanswered-questions/unansweredQuestionsSlice';
import { IPaginationPayload } from '../../../../interfaces/shared/IPaginationPayload';
import SelectCheckbox from '../../../../shared/select-checkbox';
import Button from '../../../../shared/button';
import Error from '../../../../shared/error';
import Input from '../../../../shared/input';

const QuestionsTable = ({
    hideSelect,
    onCloseModal,
}: {
    hideSelect?: boolean;
    onCloseModal?: () => void;
}) => {
    const dispatch = useAppDispatch();
    const { theme } = useTheme();
    const csvLink = React.useRef() as React.MutableRefObject<any>;
    const [rows, setRows] = useState<any[]>([]);
    const [search, setSearch] = useState<string>();
    const [sort, setSort] = useState<any>();
    const [paginationState, setPaginationState] = useState<IPaginationPayload>({
        pageNumber: 1,
        pageSize: 10,
    });
    const [submitError, setSubmitError] = useState<string>();
    const [selectedStatus, setSelectedStatus] = useState<any>();
    const [downloadQuestions, setDownloadQuestions] = useState<any>();
    const [loadingDownload, setLoadingDownload] = useState<boolean>(false);
    const [showFilters] = useState<boolean>(false);
    const paginationData = useAppSelector(
        (state) => state.questions?.questions?.data?.page,
    );
    const state = useAppSelector((state) => state.questions);

    useEffect(() => {
        if (state.questions) {
            const rows =
                state.questions?.data?.elements &&
                Array.isArray(state.questions?.data.elements)
                    ? state.questions?.data.elements.map((r) => ({
                          ...r,
                          id: r._id,
                      }))
                    : [];
            setRows(rows);
        }
    }, [state.questions]);

    const onResetPaginationState = () => {
        setPaginationState({
            pageNumber: 1,
            pageSize: paginationState?.pageSize || 10,
        });
    };

    useEffect(() => {
        const settingsFilters: any = {
            ...paginationState,
        };
        if (search) {
            settingsFilters.filters = search;
        }
        if (selectedStatus && selectedStatus?.value !== 'all') {
            settingsFilters.data = {
                status: selectedStatus?.value,
            };
        }
        if (sort) {
            settingsFilters.data = {
                ...(settingsFilters?.data || {}),
                sortBy: [{ key: 'message', value: sort }],
            };
        }
        dispatch(tryToFetchUnansweredQuestions(settingsFilters));
    }, [paginationState]);

    const onChangeSearchValue = (
        value: string | null | undefined,
        type: string,
    ) => {
        setSearch(value || undefined);
        onResetPaginationState();
    };

    const onClickSort = () => {
        if (sort) {
            setSort(sort === 'asc' ? 'desc' : 'asc');
        } else {
            setSort('asc');
        }
        onResetPaginationState();
    };

    const onDownloadQuestions = async () => {
        setLoadingDownload(true);
        try {
            const formatBody: any = {};
            if (search) {
                formatBody.filters = search;
            }
            if (selectedStatus && selectedStatus?.value !== 'all') {
                formatBody.data = {
                    status: selectedStatus?.value,
                };
            }
            if (sort) {
                formatBody.data = {
                    ...(formatBody?.data || {}),
                    sortBy: [{ key: 'message', value: sort }],
                };
            }
            const response: any = await dispatch(
                tryToFetchAllUnansweredQuestions(formatBody),
            ).unwrap();
            setDownloadQuestions(response || []);
        } catch (err) {
            setSubmitError(`${err}`);
            setLoadingDownload(false);
        }
    };

    useEffect(() => {
        if (downloadQuestions && loadingDownload) {
            csvLink.current.link.click();
        }
        setLoadingDownload(false);
    }, [downloadQuestions]);

    const onChangeStatus = (e: any, type?: string) => {
        setSelectedStatus(e);
        onResetPaginationState();
    };

    const isHTML = (str: string): boolean => {
        const doc = new DOMParser().parseFromString(str, 'text/html');
        return Array.from(doc.body.childNodes).some(
            (node) => node.nodeType === 1,
        );
    };

    return (
        <div className="">
            {submitError && <Error text={submitError} />}
            <div className="my-5 px-3 py-1 bg-[#F7F9FB] dark:bg-black flex flex-row justify-between items-center">
                <div className="flex flex-row item-center">
                    {/* <div className='cursor-pointer mr-2 hover:opacity-80'>
                        <img src={'/assets/navigation-icons/sidebar/add-icon.svg'} className='object-contain w-[18px]' />
                    </div> */}
                    {/* <div className='cursor-pointer mr-2 hover:opacity-80' onClick={onClickShowFilters}>
                        <img src={theme === 'dark' ? '/assets/insights/filters-icon-white.svg' : '/assets/insights/filters-icon.svg'} className='object-contain w-[20px]' />
                    </div> */}
                    <div
                        className="cursor-pointer mr-2 hover:opacity-80"
                        onClick={onClickSort}>
                        <img
                            src={
                                theme === 'dark'
                                    ? '/assets/insights/sort-icon-white.svg'
                                    : '/assets/insights/sort-icon.svg'
                            }
                            className="object-contain w-[20px]"
                        />
                    </div>
                </div>
                <div>
                    <Input
                        placeholder={'Search'}
                        containerStyle="mb-0 pb-0 relative dark:bg-bgColor-darkMode"
                        inputStyle={`mb-0 border-0 focus:outline-none dark:bg-bgColor-darkMode dark:text-primary-dark`}
                        icon={
                            theme === 'dark'
                                ? '/assets/navigation-icons/sidebar/search-icon-white.svg'
                                : '/assets/shared/search.svg'
                        }
                        showValue={true}
                        inputValue={search}
                        inputUniqueName={'search'}
                        onChangeInput={onChangeSearchValue}
                        hideInputSpace={true}
                    />
                </div>
            </div>
            {showFilters && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <SelectCheckbox
                        name="Answer"
                        dataQa={'select-answer'}
                        containerStyle="mb-1 min-w-full"
                        selectedOption={selectedStatus}
                        onChangeSelectedOption={(e) => onChangeStatus?.(e)}
                        options={ANSWER_STATUS || []}
                    />
                </div>
            )}
            <div className="overflow-x-auto">
                <table className="min-w-full table-auto bg-white dark:bg-black dark:text-primary-dark">
                    <thead className="bg-[#FAFAFA] dark:bg-[#3a3a3a] text-left text-sm font-medium text-black dark:text-primary-dark">
                        <tr>
                            <th className="px-4 py-3">User</th>
                            <th className="px-4 py-3">Questions</th>
                            <th className="px-4 py-3">Answers</th>
                            <th className="px-4 py-3">Reasons</th>
                            <th className="px-4 py-3">Actions</th>
                            <th className="px-4 py-3">Response</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows?.map((row) => (
                            <tr
                                key={row.id}
                                className="border-b hover:bg-gray-50 dark:hover:bg-bgColor-darkMode text-sm">
                                <td className="px-4 py-4 ">
                                    <div className="flex items-center">
                                        {row?.agent?.imageUrl &&
                                            row?.agent?.imageUrl !== '' && (
                                                <img
                                                    src={
                                                        row?.agent?.imageUrl ||
                                                        '/assets/default-avatar.png'
                                                    }
                                                    alt={row.agent?.name}
                                                    className="w-8 h-8 rounded-full"
                                                />
                                            )}
                                        <span className="ml-3 font-medium">
                                            {row.agent?.name}
                                        </span>
                                    </div>
                                </td>
                                <td className="px-4 py-4 max-w-xs break-words whitespace-normal text-sm">
                                    {row?.question}
                                </td>
                                <td className="px-4 py-4 max-w-xs break-words whitespace-normal text-sm">
                                    {row?.message &&
                                    typeof row?.message === 'string' &&
                                    isHTML(row.message) ? (
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: row?.message?.replace(
                                                    /\*\*(.*?)\*\*/g,
                                                    '<b>$1</b>',
                                                ),
                                            }}
                                        />
                                    ) : (
                                        row?.message
                                    )}
                                </td>
                                <td className="px-4 py-4">{row?.reason}</td>
                                <td className="px-4 py-4">
                                    <div className="flex items-center text-gray-600 dark:text-primary-dark">
                                        <img
                                            src={
                                                theme === 'dark'
                                                    ? '/assets/team/calendar-icon-white.svg'
                                                    : '/assets/team/calendar-icon.svg'
                                            }
                                            className="w-4 h-4 mr-1"
                                            alt=""
                                        />
                                        {moment(row.createdAt).fromNow()}
                                    </div>
                                </td>
                                <td
                                    className={`px-4 py-4 ${
                                        ANSWER_STATUS?.find(
                                            (item: any) =>
                                                item?.value === row?.type,
                                        )?.color
                                    }`}>
                                    {ANSWER_STATUS?.find(
                                        (item: any) =>
                                            item?.value === row?.type,
                                    )?.label || row?.type}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="flex justify-between items-center bg-[#F0F0F0] dark:bg-[#3a3a3a] text-sm text-[#8F8F8F] dark:text-primary-dark px-5 py-2 mt-1 border-t border-[#DCDCDD]">
                <div>
                    {paginationData && paginationData?.totalElements > 0 && (
                        <span>
                            {1 +
                                (paginationData?.number - 1) *
                                    paginationData?.size}
                            –
                            {Math.min(
                                paginationData?.number * paginationData?.size,
                                paginationData?.totalElements,
                            )}{' '}
                            of {paginationData?.totalElements} items
                        </span>
                    )}
                </div>
                <div className="flex gap-2 items-center">
                    <button
                        disabled={paginationData && paginationData?.number <= 1}
                        onClick={() =>
                            setPaginationState({
                                ...paginationState,
                                pageNumber: paginationData
                                    ? paginationData.number - 1
                                    : 1,
                            })
                        }
                        className="hover:bg-gray-300 dark:hover:bg-black cursor-pointer px-2 py-1 rounded disabled:opacity-50">
                        ◀
                    </button>
                    <span>
                        Page {paginationData ? paginationData.number : 0} of{' '}
                        {paginationData ? paginationData?.totalPages : 0}
                    </span>
                    <button
                        disabled={!paginationData?.hasNextPage}
                        onClick={() =>
                            setPaginationState({
                                ...paginationState,
                                pageNumber: paginationData
                                    ? paginationData.number + 1
                                    : 1,
                            })
                        }
                        className="hover:bg-gray-300 dark:hover:bg-black cursor-pointer px-2 py-1 rounded disabled:opacity-50">
                        ▶
                    </button>
                </div>
            </div>

            {!hideSelect && (
                <div className="flex flex-rows justify-end items-center mt-10">
                    {/* @ts-ignore  */}
                    <CSVLink
                        ref={csvLink}
                        data={
                            downloadQuestions?.filter(
                                (item: any, idx: number, arr: any) => {
                                    return idx > 0;
                                },
                            ) || []
                        }
                        headers={downloadQuestions?.[0] || []}
                        className={'none'}
                        target={'_blank'}
                        filename={`Unanswered-Questions.csv`}></CSVLink>
                    <Button
                        className="btn-border-rounded !shadow-none min-w-[130px] mr-3 dark:border-white dark:text-white"
                        label={
                            loadingDownload
                                ? 'Preparing data'
                                : 'Download full report .csv'
                        }
                        onClickButton={() =>
                            !loadingDownload && onDownloadQuestions()
                        }
                        dataQa={'download-btn'}
                    />
                </div>
            )}
        </div>
    );
};

export default QuestionsTable;
